// better-auth's refusals (`{ code, message }`, spec/08 §2.6) → the console's copy. The answer never
// says whether an account exists (spec/08 §2.6): a wrong password and an unknown address read alike.

import { useTranslation } from '@clavian/i18n/react';
import { useCallback } from 'react';

import { problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';

export function authFailureKey(error: unknown): string {
  switch (problemCode(error)) {
    case 'INVALID_EMAIL_OR_PASSWORD':
    case 'INVALID_USERNAME_OR_PASSWORD':
    case 'INVALID_PASSWORD':
      return 'signIn.failure.credentials';
    case 'INVALID_OTP':
    case 'OTP_EXPIRED':
    case 'OTP_NOT_FOUND':
    case 'TOO_MANY_ATTEMPTS':
    case 'INVALID_CODE':
    case 'INVALID_BACKUP_CODE':
    case 'INVALID_TWO_FACTOR_COOKIE':
      return 'signIn.failure.code';
    case 'RATE_LIMITED':
      return 'signIn.failure.rateLimited';
    case 'BANNED_USER':
      return 'signIn.failure.banned';
    case 'EMAIL_NOT_VERIFIED':
      return 'signIn.failure.notVerified';
    case 'OTP_DELIVERY_UNAVAILABLE':
      return 'signIn.failure.delivery';
    // Phone sign-in turned off on the Platform (`auth.phoneSignIn`): only a page that still showed the phone path.
    case 'PHONE_SIGN_IN_OFF':
      return 'signIn.failure.phoneOff';
    case 'INVALID_PHONE_NUMBER':
    case 'PREMIUM_RATE_NUMBER':
      return 'signIn.invalidPhone';
    // A number with no account would create a player account (spec/07 §3.1); staff are invited
    // (§6.5), so the console never offers that.
    case 'CONSENT_REQUIRED':
      return 'signIn.failure.noAccount';
    default:
      return 'errors.generic';
  }
}

/**
 * `/v1/auth/sign-up/email`'s and `/v1/auth/email-otp/verify-email`'s refusals → the sign-up screen's
 * copy (spec/07 §3.1, F-CMP-06). Separate from `authFailureKey`: `CONSENT_REQUIRED` reads there as
 * "no staff account uses this number", which is sign-in's answer, not sign-up's.
 */
export function signUpFailureKey(error: unknown): string {
  switch (problemCode(error)) {
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'signUp.failure.emailTaken';
    case 'INVALID_EMAIL':
      return 'signUp.failure.invalidEmail';
    case 'PASSWORD_TOO_SHORT':
      return 'signUp.failure.passwordTooShort';
    case 'PASSWORD_TOO_LONG':
      return 'signUp.failure.passwordTooLong';
    case 'PASSWORD_COMPROMISED':
      return 'signUp.failure.passwordCompromised';
    case 'CONSENT_REQUIRED':
    case 'CONSENT_VERSION_OUTDATED':
      return 'signUp.failure.consentRequired';
    case 'SIGN_UP_REFUSED':
      return 'signUp.failure.refused';
    case 'INVALID_OTP':
    case 'OTP_EXPIRED':
    case 'OTP_NOT_FOUND':
    case 'TOO_MANY_ATTEMPTS':
      return 'signIn.failure.code';
    case 'RATE_LIMITED':
      return 'signIn.failure.rateLimited';
    default:
      return 'errors.generic';
  }
}

/**
 * The sentence for a refused sign-in step. better-auth's own UPPER_SNAKE codes keep their screen-specific lines; any
 * other answer (the API's catalogue codes such as `otp_invalid` or `rate_limited`, a 5xx, no connection) reads the shared
 * copy for its problem code, so nothing falls back to the generic line while the server said something specific.
 */
export function useAuthFailure(kind: 'signIn' | 'signUp' = 'signIn'): (error: unknown, override?: string) => string {
  const { t } = useTranslation('console');
  const problem = useProblem();
  return useCallback(
    (error, override) => {
      if (override !== undefined) return override;
      const key = kind === 'signIn' ? authFailureKey(error) : signUpFailureKey(error);
      return key === 'errors.generic' ? problem.text(error, { fallback: t('errors.generic') }) : t(key);
    },
    [kind, problem, t],
  );
}
